/**
 * StrategicLogicTree - Hierarchical visualization of Strategic Blueprint workflow
 *
 * Displays the strategic research process as a "Living Blueprint" that grows:
 * - User -> Engagement Manager -> Data Anchor -> Workstreams (parallel) -> Synthesis
 * - Hypothesis Stress Test -> AI Council Debate -> Mediator -> Final Aggregator
 *
 * Sub-components extracted into ../strategic/streaming/ directory.
 */

import { useRef, useEffect, useCallback } from 'react';
import { cn } from '@/lib/utils';
import { useIsMobile, useIsTablet } from '@/hooks/useMediaQuery';
import { ExportMenu } from './ExportMenu';
import { handleStrategicExport, type StrategicExportOptions } from '@/utils/exportUtils';
import type { StrategicStreamingState } from '@/hooks/useStrategicStream';
import type { ExportFormat } from '@/types/researchModes';

// New components
import { PhaseProgressBar } from '@/components/strategic/PhaseProgressBar';

// Extracted sub-components
import { StrategicHeader } from '@/components/strategic/streaming/StrategicHeader';
import { SvgConnector } from '@/components/strategic/streaming/SvgConnector';
import { UserNode } from '@/components/strategic/streaming/UserNode';
import { EngagementManagerNode } from '@/components/strategic/streaming/EngagementManagerNode';
import { DataAnchorNode } from '@/components/strategic/streaming/DataAnchorNode';
import { WorkstreamsGrid } from '@/components/strategic/streaming/WorkstreamsGrid';
import { ConflictMapperNode } from '@/components/strategic/streaming/ConflictMapperNode';
import { HypothesisStressTestContainer } from '@/components/strategic/streaming/HypothesisStressTestContainer';
import { StrategicDeliverableCard } from '@/components/strategic/streaming/StrategicDeliverableCard';

export interface StrategicLogicTreeProps {
  state: StrategicStreamingState;
  query: string;
  className?: string;
  onNewResearch?: () => void;
  /** When true, hides export menus and action buttons (used in showcase/landing page) */
  hideActions?: boolean;
}

export function StrategicLogicTree({ state, query, className, onNewResearch, hideActions }: StrategicLogicTreeProps) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const isSmallScreen = isMobile || isTablet;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { phase, intensity, workstreams, conflicts, stressTestResults, debateArguments, mediatorConsensus, deliverable, competitorData, ghostDeck } = state;

  // Auto-scroll to keep active content in view (disabled on landing page)
  useEffect(() => {
    if (hideActions) return;
    if (scrollContainerRef.current && phase !== 'idle') {
      scrollContainerRef.current.scrollTo({ top: scrollContainerRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [phase, workstreams.length, debateArguments.length, hideActions]);

  // Export handler
  const handleExport = useCallback(async (format: ExportFormat): Promise<void> => {
    const exportOptions: StrategicExportOptions = {
      query,
      topic: state.topic || query,
      intensity,
      workstreams,
      conflicts,
      stressTestResults,
      debateArguments,
      debateRounds: state.debateRounds,
      mediatorConsensus,
      ghostDeck: state.ghostDeck,
      deliverable,
      competitorData,
      startedAt: state.startedAt,
      completedAt: state.completedAt,
    };
    await handleStrategicExport(format, exportOptions);
  }, [query, state.topic, state.debateRounds, state.ghostDeck, state.startedAt, state.completedAt, intensity, workstreams, conflicts, stressTestResults, debateArguments, mediatorConsensus, deliverable, competitorData]);

  return (
    <div className={cn('flex flex-col h-full bg-surface rounded-2xl border border-border-subtle overflow-x-hidden', className)}>
      {/* Header */}
      <StrategicHeader intensity={intensity} phase={phase} isSmallScreen={isSmallScreen} effectiveIntensity={state.effectiveIntensity} estimatedCredits={state.estimatedCredits} />

      {/* Phase Progress Bar */}
      {phase !== 'idle' && (
        <div className={cn('border-b border-border-subtle bg-bar-bg/30', isSmallScreen ? 'px-3 py-2' : 'px-6 py-3')}>
          <PhaseProgressBar phase={phase} isSmallScreen={isSmallScreen} startedAt={state.startedAt} />
        </div>
      )}

      {/* Scrollable Content */}
      <div ref={scrollContainerRef} className={cn('flex-1', isSmallScreen ? 'p-3' : 'p-6')}>
        <div className="flex flex-col items-center gap-5">
          {/* User Node */}
          <UserNode query={query} isSmallScreen={isSmallScreen} />
          <SvgConnector animated={phase === 'planning' || phase === 'engagement_manager'} />

          {/* Engagement Manager - MECE Decomposition */}
          {phase !== 'idle' && (
            <>
              <EngagementManagerNode
                isActive={phase === 'engagement_manager'}
                isComplete={['data_anchoring', 'workstreams', 'synthesis', 'stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase)}
                workstreamCount={workstreams.length}
                workstreams={workstreams}
              />
              <SvgConnector animated={phase === 'engagement_manager' || phase === 'data_anchoring'} />
            </>
          )}

          {/* Data Anchor - Market Intelligence */}
          {['data_anchoring', 'workstreams', 'synthesis', 'stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase) && (
            <>
              <DataAnchorNode
                phase={phase}
                workstreamCount={workstreams.length}
                competitorData={competitorData}
                isSmallScreen={isSmallScreen}
              />
              <SvgConnector animated={phase === 'data_anchoring' || phase === 'workstreams'} />
            </>
          )}

          {/* Workstreams Execution - Parallel Research */}
          {['workstreams', 'synthesis', 'stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase) && workstreams.length > 0 && (
            <>
              <WorkstreamsGrid
                workstreams={workstreams}
                phase={phase}
                isSmallScreen={isSmallScreen}
              />
              <SvgConnector animated={phase === 'workstreams' || phase === 'synthesis'} />
            </>
          )}

          {/* Synthesis & Conflict Mapper */}
          {['synthesis', 'stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase) && (
            <>
              <ConflictMapperNode phase={phase} conflicts={conflicts} workstreams={workstreams} isSmallScreen={isSmallScreen} />
              <SvgConnector animated={phase === 'synthesis' || phase === 'stress_test'} />
            </>
          )}

          {/* Hypothesis Stress Test Container - Encompasses Stress Test, Debate, and Mediator */}
          {['stress_test', 'debate', 'mediation', 'aggregation', 'concluded'].includes(phase) && (
            <>
              <HypothesisStressTestContainer
                phase={phase}
                stressTestResults={stressTestResults}
                debateArguments={debateArguments}
                mediatorConsensus={mediatorConsensus}
                isSmallScreen={isSmallScreen}
              />
              <SvgConnector animated={phase === 'mediation' || phase === 'aggregation'} />
            </>
          )}

          {/* Strategic Deliverable - Final Aggregator + Executive Summary */}
          {['aggregation', 'concluded'].includes(phase) && (
            <StrategicDeliverableCard
              phase={phase}
              deliverable={deliverable ?? null}
              stressTestResults={stressTestResults}
              ghostDeck={ghostDeck}
              isSmallScreen={isSmallScreen}
            />
          )}
        </div>
      </div>

      {/* Footer with export and new research */}
      {phase === 'concluded' && !hideActions && (
        <div className={cn('border-t border-border-subtle bg-bar-bg/50 flex items-center justify-between', isSmallScreen ? 'px-3 py-2' : 'px-6 py-3')}>
          <ExportMenu onExport={handleExport} disabled={false} />
          {onNewResearch && (
            <button onClick={onNewResearch}
              className={cn('px-3 py-1.5 rounded-lg bg-blue-500/20 border border-blue-500/30 text-blue-400 hover:bg-blue-500/30 transition-colors', isSmallScreen ? 'text-xs' : 'text-sm')}>
              New Research
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default StrategicLogicTree;
